import { ArrowDown, ArrowUp, Eye, EyeOff, LayoutPanelTop, RotateCcw, Settings2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DEFAULT_DESK_PREFERENCES, moveDeskWidget, normalizeDeskPreferences, type DeskPreferences, type DeskWidgetId } from "@/lib/dashboardWorkspace";

export type DesktopDeskWidget = {
  id: DeskWidgetId;
  title: string;
  value: string;
  description: string;
  icon: React.ElementType;
  tone: string;
  onClick: () => void;
};

function storageKey(salonId?: number) {
  return `beauty:desktop-operation-desk:${salonId ?? "current"}`;
}

export function DesktopOperationDesk({ salonId, widgets }: { salonId?: number; widgets: DesktopDeskWidget[] }) {
  const [preferences, setPreferences] = useState<DeskPreferences>(DEFAULT_DESK_PREFERENCES);
  const [customizing, setCustomizing] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey(salonId));
      setPreferences(normalizeDeskPreferences(stored ? JSON.parse(stored) : undefined));
    } catch {
      setPreferences(DEFAULT_DESK_PREFERENCES);
    }
  }, [salonId]);

  const updatePreferences = (next: DeskPreferences) => {
    const normalized = normalizeDeskPreferences(next);
    setPreferences(normalized);
    localStorage.setItem(storageKey(salonId), JSON.stringify(normalized));
  };
  const orderedWidgets = useMemo(() => preferences.order.map((id) => widgets.find((widget) => widget.id === id)).filter(Boolean) as DesktopDeskWidget[], [preferences.order, widgets]);

  return <section className="hidden rounded-2xl border border-border bg-card/70 p-4 shadow-sm md:block">
    <div className="mb-3 flex items-center justify-between gap-3">
      <div><p className="flex items-center gap-2 text-sm font-semibold"><LayoutPanelTop className="h-4 w-4 text-primary" />Mesa de operação</p><p className="text-xs text-muted-foreground">Organize os blocos que ajudam você a decidir o próximo passo.</p></div>
      <Button variant="outline" size="sm" className="gap-2" onClick={() => setCustomizing(true)}><Settings2 className="h-4 w-4" />Personalizar</Button>
    </div>
    <div className="grid gap-3 lg:grid-cols-4">
      {orderedWidgets.filter((widget) => !preferences.hidden.includes(widget.id)).map((widget) => {
        const Icon = widget.icon;
        return <button key={widget.id} type="button" onClick={widget.onClick} className="group rounded-xl border bg-background p-3 text-left transition-colors hover:border-primary/35 hover:bg-accent/25"><span className={`flex h-8 w-8 items-center justify-center rounded-lg ${widget.tone}`}><Icon className="h-4 w-4" /></span><span className="mt-3 block text-xl font-semibold">{widget.value}</span><span className="block text-sm font-medium">{widget.title}</span><span className="mt-1 block truncate text-xs text-muted-foreground">{widget.description}</span></button>;
      })}
      {preferences.hidden.length === widgets.length && <Card className="col-span-full"><CardContent className="p-4 text-sm text-muted-foreground">Todos os blocos estão ocultos. Use Personalizar para restaurar a mesa recomendada.</CardContent></Card>}
    </div>
    <Sheet open={customizing} onOpenChange={setCustomizing}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader><SheetTitle>Personalizar mesa de operação</SheetTitle></SheetHeader>
        <p className="mt-2 text-sm text-muted-foreground">Defina a ordem e os blocos visíveis. Esta preferência fica salva somente para a sua operação.</p>
        <div className="mt-5 space-y-2">
          {preferences.order.map((id, index) => {
            const widget = widgets.find((item) => item.id === id);
            if (!widget) return null;
            const hidden = preferences.hidden.includes(id);
            return <div key={id} className="flex items-center gap-2 rounded-xl border p-3"><div className="min-w-0 flex-1"><p className="text-sm font-medium">{widget.title}</p><p className="truncate text-xs text-muted-foreground">{widget.description}</p></div><Button size="icon" variant="ghost" className="h-8 w-8" disabled={index === 0} onClick={() => updatePreferences({ ...preferences, order: moveDeskWidget(preferences.order, id, "up") })} aria-label={`Subir ${widget.title}`}><ArrowUp className="h-4 w-4" /></Button><Button size="icon" variant="ghost" className="h-8 w-8" disabled={index === preferences.order.length - 1} onClick={() => updatePreferences({ ...preferences, order: moveDeskWidget(preferences.order, id, "down") })} aria-label={`Descer ${widget.title}`}><ArrowDown className="h-4 w-4" /></Button><Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => updatePreferences({ ...preferences, hidden: hidden ? preferences.hidden.filter((item) => item !== id) : [...preferences.hidden, id] })} aria-label={hidden ? `Mostrar ${widget.title}` : `Ocultar ${widget.title}`}>{hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button></div>;
          })}
        </div>
        <Button variant="outline" className="mt-5 w-full gap-2" onClick={() => updatePreferences(DEFAULT_DESK_PREFERENCES)}><RotateCcw className="h-4 w-4" />Restaurar layout recomendado</Button>
      </SheetContent>
    </Sheet>
  </section>;
}
